import React, { forwardRef, useContext } from 'react';
import { getSpacing } from '../../../utils/spacing';
import { View, StyleProp, ViewStyle, LayoutChangeEvent } from 'react-native';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { ForceStackFieldsContext } from '../Form/ForcedStackFieldsContext';

export type Justify = 'flex-start' | 'center' | 'flex-end';

export type HorizontalProps = {
    horizontal: true;
};

export type VerticalProps = {
    vertical: true;
};

type FlattenedChildrenResultItem = {
    node: React.ReactNode;
    key: React.Key | null;
};

/**
 * Recursively flatten out children stripping fragments.  This is so
 * <Stack space={20}>
 *  <Fragment>
 *     {item1}
 *     {item2}
 *  </Fragment>
 *  <Fragment>
 *     {item3}
 *     {item4}
 *  </Fragment>
 * </Stack>
 *
 * Will leave spacing between all items and not just between the fragments.
 *
 * The keys are tricky here.  If we just render the default key, item1 and item3 with have conflicting keys of .0 (2 and 4 also conflict).
 * To work around this, we prepend the key of the parent fragment to the child key and the resulting item keys are:
 * .0.0, .0.1, .1.0, .1.1
 */
const flattenChildren = (children: React.ReactNode, baseKey?: React.Key): FlattenedChildrenResultItem[] => {
    return React.Children.toArray(children).flatMap((child) => {
        if (React.isValidElement<{ children?: React.ReactNode }>(child) && child.type === React.Fragment) {
            return flattenChildren(child.props.children, child.key || undefined);
        }

        let childKey = React.isValidElement(child) ? child.key : null;
        if (baseKey !== undefined && childKey !== null) {
            childKey = String(baseKey) + String(childKey);
        }

        return [
            {
                node: child,
                key: childKey,
            },
        ];
    });
};

export type StackProps = {
    children: React.ReactNode | React.ReactNode[];
    space?: number;
    justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
    align?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
    style?: StyleProp<ViewStyle>;
    fullHeight?: boolean;
    fullWidth?: boolean;
    testID?: string;
    flex?: number;
    padding?: number;
    onLayout?: (event: LayoutChangeEvent) => void;
    renderDivider?: () => React.ReactNode;
    logId?: string;
} & (HorizontalProps | VerticalProps);

const Stack = forwardRef<View, StackProps>((props, ref) => {
    const isVertical = 'vertical' in props;
    const spacing = getSpacing(props.space || 0);

    const renderContent = () => {
        const asArray = flattenChildren(props.children);
        const validElements = asArray.filter((_) => React.isValidElement(_.node));
        const mapped = validElements.map((child, index) => {
            const isFirst = index === 0;
            const spacerStile = isVertical ? { height: spacing } : { width: spacing };
            const renderDivider = () => {
                if (isFirst) {
                    return null;
                } else if (props.renderDivider) {
                    return <React.Fragment key={`${index + '-divider'}`}>{props.renderDivider()}</React.Fragment>;
                } else {
                    return <View style={spacerStile} testID="divider" />;
                }
            };

            return (
                <React.Fragment key={child.key || index}>
                    {renderDivider()}
                    {child.node}
                </React.Fragment>
            );
        });
        return mapped;
    };

    return (
        <View
            style={[
                {
                    flexDirection: isVertical ? 'column' : 'row',
                    justifyContent: props.justify || 'flex-start',
                    alignItems: props.align || 'flex-start',
                },
                {
                    height: props.fullHeight ? '100%' : undefined,
                    width: props.fullWidth ? '100%' : undefined,
                },
                {
                    flex: props.flex,
                },
                {
                    padding: props.padding !== undefined ? getSpacing(props.padding) : undefined,
                },
                props.style,
            ]}
            testID={props.testID}
            onLayout={props.onLayout}
            collapsable={false}
            ref={ref}
        >
            {renderContent()}
        </View>
    );
});

export default Stack;

export const contentStackSpacing = 5;

const BaseContentStack = (props: Pick<StackProps, 'children' | 'testID'> & { spacing: number }) => {
    return (
        <Stack
            vertical
            space={props.spacing}
            fullWidth
            justify="flex-start"
            style={{ flex: 1 }}
            testID={props.testID || 'content-stack'}
        >
            {props.children}
        </Stack>
    );
};

/**
 * Standard stack layout for screen contents
 */
export const ContentStack: React.FC<Pick<StackProps, 'children' | 'testID'>> = (props) => {
    return <BaseContentStack spacing={contentStackSpacing} {...props} />;
};

export const FormStack: React.FC<Pick<StackProps, 'children' | 'testID' | 'style'>> = (props) => {
    const isMobile = useIsMobileLayout();
    const forceStackFields = useContext(ForceStackFieldsContext);
    return <BaseContentStack spacing={contentStackSpacing * (isMobile || forceStackFields ? 1 : 2)} {...props} />;
};
